/**
 * @fileoverview Main App component for SoiledIt
 * @module App
 * @author SoiledIt Development Team
 * @version 1.0.3
 */

import React from 'react';
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import PlantSearch from './components/PlantSearch';
import SoilMixDetails from './components/SoilMixDetails';
import ProductList from './components/ProductList';
import ProductDetails from './components/ProductDetails';
import CartPage from './components/pages/CartPage';
import CartIcon from './components/CartIcon';
import Home from './components/pages/Home';
import Checkout from './components/Checkout';
import OrderConfirmation from './components/OrderConfirmation';
import { CartProvider } from './context/CartContext';
import AdminDashboard from './components/AdminDashboard';
import usePageTracking from './hooks/usePageTracking';
import './App.css';

/**
 * Load Stripe outside of component render cycle for better performance
 * Uses environment variable to support both development and production keys
 * Development: Uses test keys from local .env file
 * Production: Uses live keys from Vercel environment variables
 */
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function TrackedRoutes() {
  usePageTracking(); // ✅ fires a GA page_view on route changes

  return (
    <Routes>
      {/* Home page route */}
      <Route path="/" element={<Home />} />

      {/* Plant search route */}
      <Route path="/plants" element={<PlantSearch />} />

      {/* Soil mix details route - typically accessed from plant search */}
      <Route path="/soil-mix/:id" element={<SoilMixDetails />} />

      {/* Products listing route */}
      <Route path="/products" element={<ProductList />} />

      {/* Product details route */}
      <Route path="/products/:id" element={<ProductDetails />} />

      {/* Shopping cart route */}
      <Route path="/cart" element={<CartPage />} />

      {/* Admin Page Route */}
      <Route path="/admin" element={<AdminDashboard />} />

      {/* Checkout route */}
      <Route
        path="/checkout"
        element={
          <Elements stripe={stripePromise} options={{}}>
            <Checkout />
          </Elements>
        }
      />

      {/* Order confirmation route */}
      <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
    </Routes>
  );
}


/**
 * Main App component
 * Provides routing and context providers for the entire application
 * 
 * @component
 * @returns {JSX.Element} Rendered App component
 */
function App() {
  return (
    <CartProvider>
      <Router>
        <div className="App">
          <header className="app-header">
            <div className="header-content">
              <div className="logo">
                <Link to="/">SoiledIt</Link>
              </div>
              <nav className="main-nav">
                <ul>
                  <li><Link to="/">Home</Link></li>
                  <li><Link to="/plants">Plant Search</Link></li>
                  <li><Link to="/products">Soil Mixes</Link></li>
                </ul>
              </nav>
              <div className="header-actions">
                <CartIcon />
              </div>
            </div>
          </header>

          <main className="app-main">
            {/* ✅ tracked routes */}
            <TrackedRoutes />
          </main>

          <footer className="app-footer">
            <div className="footer-content">
              <div className="footer-section">
                <h3>About SoiledIt</h3>
                <p>
                  At SoiledIt, we believe gardening should bring joy, not frustration.
                  As fellow plant lovers, we understand the struggle: standing in garden centers overwhelmed by soil options, buying more than you need, choosing the wrong mix, and cleaning up dirt trails through your home.
                  That's why we created something better. Custom soil mixes formulated specifically for your plants and delivered in perfect portions to your door. Because when your plants thrive, you thrive too.
                </p>
              </div>

              <div className="footer-section">
                <h3>Quick Links</h3>
                <ul>
                  <li><Link to="/">Home</Link></li>
                  <li><Link to="/plants">Plant Search</Link></li>
                  <li><Link to="/products">Soil Mixes</Link></li>
                  <li><Link to="/cart">Your Cart</Link></li>
                </ul>
              </div>

              <div className="footer-section">
                <h3>Contact Us</h3>
                <p>Email: soiledit.business@gmail.com</p>
                {/* <p>Phone: (111) 111-1111</p>*/ }
              </div>
            </div>

            <div className="copyright">
              <p>&copy; {new Date().getFullYear()} SoiledIt - All rights reserved</p>
            </div>
          </footer>
        </div>
      </Router>
    </CartProvider>
  );
}

export default App;